import { mainApiUrl, baseHeaders, storeId } from '@constants'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getStateFromLocalStorage, storeStateAtLocalStorage } from '@src/reduxjs'
import { bugsnagSetUser } from '@src/utils'
import jwtDecode from 'jwt-decode'
import { t } from 'react-i18nify'

const initialState = {
  user: {
    id: '',
    email: '',
    name: '',
    permissions: {},
    picture_url: '',
    role: '',
    store: {},
  },
  jwtToken: '',
  message: '',
  inProgress: false,
  store: {
    id: null,
    secret_key: null,
  }
}

export const loginThunk = createAsyncThunk('auth/loginThunk', async (data, { rejectWithValue }) => {
  const body = JSON.stringify({ ...data, store_id: storeId })

  const response = await fetch(`${mainApiUrl}/login`, { body, headers: baseHeaders, method: 'POST' })

  if (response.status === 400) {
    const message = t('invalid_user_or_pass')
    return rejectWithValue(message)
  }

  if (response.status === 401) {
    const message = t('unauthorized')
    return rejectWithValue(message)
  }

  return await response.json()
})

export const lostPasswordThunk = createAsyncThunk('auth/lostPasswordThunk', async (data, { rejectWithValue }) => {
  const body = JSON.stringify({ ...data, store_id: storeId })

  const response = await fetch(`${mainApiUrl}/login/recover`, { body, headers: baseHeaders, method: 'POST' })

  if (response.status !== 200) {
    const json = await response.json()
    return rejectWithValue(t(json.message || 'error'))
  }

  return await response.json()
})

export const getUserThunk = createAsyncThunk('auth/getUserThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/users/me`, { body, headers, method: 'GET' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

export const updateUserThunk = createAsyncThunk('auth/updateUserThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }
  const response = await fetch(`${mainApiUrl}/users/me`, { body, headers, method: 'PUT' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

const authSlice = createSlice({
  name: 'auth',
  initialState: { ...initialState, ...getStateFromLocalStorage('authSlice') },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload || initialState.user
    },
    setJwtToken: (state, action) => {
      state.jwtToken = action.payload || initialState.jwtToken
    },
    setMessage: (state, action) => {
      state.message = action.payload || initialState.message
    },
    logout: (state, action) => {
      const storedLocale = window.localStorage.getItem(`ff:locale`)

      window.localStorage.clear()

      if (storedLocale) {
        window.localStorage.setItem(`ff:locale`, storedLocale)
      }

      return initialState
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(({ type }) => type === `${loginThunk.pending}`, (state) => {
      state.inProgress = true
    })
    .addMatcher(({ type }) => type === `${loginThunk.fulfilled}`, (state, action) => {
      state.inProgress = false
      state.jwtToken = action.payload.data
      state.user = action.payload.data ? jwtDecode(action.payload.data) : {}
      state.message = t('authorized')
      bugsnagSetUser(state.user.id, state.user.email, state.user.name)
      storeStateAtLocalStorage('authSlice', { user: state.user, jwtToken: state.jwtToken })
    })
      .addMatcher(({ type }) => type === `${loginThunk.rejected}`, (state, action) => {
      state.message = action.payload || ''
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${updateUserThunk.pending}`, (state) => {
      state.inProgress = true
    })
      .addMatcher(({ type }) => type === `${updateUserThunk.fulfilled}`, (state, action) => {
      state.inProgress = false
      state.user = action.payload.data
      storeStateAtLocalStorage('authSlice', { user: state.user })
    })
    .addMatcher(({ type }) => type === `${updateUserThunk.rejected}`, (state, action) => {
      state.inProgress = false
    })
      .addMatcher(({ type }) => type === `${getUserThunk.pending}`, (state) => {
      state.inProgress = true
    })
    .addMatcher(({ type }) => type === `${getUserThunk.fulfilled}`, (state, action) => {
      state.inProgress = false
      state.user = action.payload.data
      bugsnagSetUser(state.user.id, state.user.email, state.user.name)
      storeStateAtLocalStorage('authSlice', { user: state.user })
    })
      .addMatcher(({ type }) => type === `${getUserThunk.rejected}`, (state, action) => {
      state.inProgress = false
    })
    .addMatcher(({ type }) => type === `${lostPasswordThunk.pending}`, (state) => {
      state.inProgress = true
    })
      .addMatcher(({ type }) => type === `${lostPasswordThunk.fulfilled}`, (state, action) => {
      state.inProgress = false
      state.message = t(action.payload.message || 'ok')
    })
    .addMatcher(({ type }) => type === `${lostPasswordThunk.rejected}`, (state, action) => {
      state.inProgress = false
      state.message = action.payload || ''
    })
  },
})

export const { setUser, setJwtToken, setMessage, logout } = authSlice.actions

export default authSlice
